import React, { useCallback, useMemo } from 'react';
import { Table, Button, TableColumnProps } from 'antd';
import {
  ClearOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CustomCard from '@components/CustomCard';
import EditableCell, { IEditableCell } from './EditableCell';
import EditableRow from './EditableRow';

export interface IEditableTableColumns
  extends TableColumnProps<any>,
    IEditableCell {
  dataIndex: any;
  children?: any;
  onCell?: any;
}

interface EditableTableProps {
  columns: IEditableTableColumns[];
  data: any[];
  onChange?: (values: any[]) => void;
  title?: string;
  tableKey?: string;
  allowAddRows?: boolean;
  allowDelete?: boolean;
  disabledAddRows?: boolean;
}

const EditableTable = ({
  columns,
  data,
  onChange,
  title,
  tableKey,
  allowAddRows = false,
  allowDelete = false,
  disabledAddRows = false,
}: EditableTableProps) => {
  const { t } = useTranslation('table');

  const newRow = useMemo(
    () =>
      columns?.reduce((accumulator, currentValue) => {
        // @ts-ignore
        accumulator[currentValue.dataIndex] = undefined;
        return accumulator;
      }, {}),
    [columns],
  );

  const tableData = useMemo(() => {
    if (data?.length > 0) {
      return data?.map((item, index) => ({
        ...item,
        key: index,
      }));
    }
    return [
      {
        ...newRow,
        key: 0,
      },
    ];
  }, [data, newRow]);

  const handleDelete = useCallback(
    (key: any) => {
      const newData = tableData.filter(item => item.key !== key);
      onChange?.(newData);
    },
    [tableData, onChange],
  );

  const handleAdd = useCallback(() => {
    onChange?.([...tableData, newRow]);
  }, [newRow, onChange, tableData]);

  const handleSave = useCallback(
    (row: Record<string, string | number>) => {
      const updatedTableData = tableData.map(item => {
        if (item.key === row.key) {
          return { ...item, ...row };
        }
        return item;
      });
      onChange?.(updatedTableData);
    },
    [tableData, onChange],
  );

  const tableColumns: IEditableTableColumns[] = useMemo(() => {
    const allColumns: IEditableTableColumns[] = [...columns];

    if (allowDelete) {
      allColumns.push({
        align: 'center',
        dataIndex: 'actions',
        render: (_: any, record: any) =>
          tableData.length > 0 && (
            <Button
              type="link"
              size="large"
              danger
              onClick={() => handleDelete(record.key)}
            >
              {tableData?.length > 1 ? <DeleteOutlined /> : <ClearOutlined />}
            </Button>
          ),
      });
    }

    return allColumns.map(col => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record: any) => ({
          record,
          handleSave,
          ...col,
        }),
      };
    });
  }, [columns, allowDelete, tableData.length, handleDelete, handleSave]);

  return (
    <CustomCard
      title={title}
      headStyle={{
        color: 'var(--primary-color)',
        fontSize: '0.9rem',
      }}
      bodyStyle={{
        padding: 0,
      }}
    >
      <Table
        dataSource={tableData}
        columns={tableColumns}
        components={{
          body: {
            row: EditableRow,
            cell: EditableCell,
          },
        }}
        rowKey={tableKey}
        style={{ width: '100%', overflow: 'auto' }}
        pagination={false}
        bordered
      />
      {allowAddRows && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: 14,
            opacity: 0.7,
          }}
        >
          <Button
            onClick={handleAdd}
            icon={<PlusCircleOutlined />}
            disabled={disabledAddRows}
          >
            {t('editable.add-row')}
          </Button>
        </div>
      )}
    </CustomCard>
  );
};

export default EditableTable;
